import type { AccessorFn, SortingState, Cell, Row } from '@tanstack/react-table';
import { useReactTable, getCoreRowModel, createColumnHelper, getSortedRowModel } from '@tanstack/react-table';
import * as React from 'react';

import { useDraggableColumns } from 'af-utils/react.util';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

const CELL_MIN_WIDTH = 100;
const DISPLAY_CELL_WIDTH = 50;
const ACCESSOR_CELL_WIDTH = '1fr';

import BaseCell from './BaseCell';
import BaseHeaderCell from './BaseHeaderCell';
import styles from './styles.module.scss';
import type { FooterButton, SimpleTableProps } from './types';

const EMPTY_ROWS: never[] = [];

function SimpleTable<T>(props: React.PropsWithChildren<SimpleTableProps<T>>): React.ReactElement {
	const {
		rows,
		columns,
		label,
		labelTooltip,
		footerButtonsLeft = [],
		footerButtonsRight = [],
		headerButtons = [],
		disableAllSortings = false,
		rowClassName,
		bottomRows,
		footerComponent,
		getRowId,
		emptyTableMessage,
		onRowDoubleClick,
		highlightDeletedRows = false,
	} = props;
	const [tableGridLayout, setTableGridLayout] = React.useState<Record<string, string>>({});
	const { cellWidthsMap, columnRefs, onColumnDrag } = useDraggableColumns({
		minColumnWidth: CELL_MIN_WIDTH,
	});

	const [sorting, setSorting] = React.useState<SortingState>([]);
	const setHeaderReference = React.useCallback((headerId: string) => (_ref: HTMLSpanElement) => { columnRefs.current[headerId] = _ref; }, [columnRefs]);

	const columnDefs = React.useMemo(() => {
		const columnHelper = createColumnHelper<T>();

		return columns.map((_column) => {
			if (_column.isDisplayColumn) {
				return columnHelper.display({
					id: _column.id,
					cell: _column.cell,
					header: _column.header ?? '',
					size: _column.size,
				});
			}

			return columnHelper.accessor(_column.accessor as unknown as AccessorFn<T>, {
				id: _column.id,
				cell: _column.cell,
				header: _column.header ?? '',
				size: _column.size,
				enableSorting: _column.enableSorting,
				sortingFn: _column.sortingFn ?? 'auto',
				sortDescFirst: _column.sortDescFirst,
			});
		});

	}, [columns]);

	const table = useReactTable({
		columns: columnDefs,
		data: rows ?? EMPTY_ROWS,
		getSortedRowModel: getSortedRowModel(),
		getCoreRowModel: getCoreRowModel(),
		enableMultiSort: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		state: { sorting },
		getRowId,
	});

	const bottomTable = useReactTable({
		columns: columnDefs,
		data: bottomRows ?? EMPTY_ROWS,
		getCoreRowModel: getCoreRowModel(),
	});

	React.useEffect(() => {
		const newTableGridLayoutMap: Record<string, string> = {};

		for (const _column of table.getAllColumns()) {
			const isDisplayColumn = !_column.accessorFn;
			let cellWidth = '';
			if (!!_column.columnDef.size) {
				cellWidth = (typeof _column.columnDef.size === 'number') ? `${_column.columnDef.size}px` : _column.columnDef.size;
			} else {
				cellWidth = isDisplayColumn ? `${DISPLAY_CELL_WIDTH}px` : `${ACCESSOR_CELL_WIDTH}`;
			}
			newTableGridLayoutMap[_column.id] = _column.columnDef.meta?.isHidden ? '' : cellWidth;
		}

		setTableGridLayout(newTableGridLayoutMap);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnDefs]);

	const gridTemplateColumns = React.useMemo(() => {
		const newTableGridLayoutMap = {};
		for (const _headerId of Object.keys(tableGridLayout)) {
			newTableGridLayoutMap[_headerId] = cellWidthsMap[_headerId]?.width
				? `${cellWidthsMap[_headerId]?.width}px`
				: tableGridLayout[_headerId];
		}

		return Object.values(newTableGridLayoutMap).join(' ');
	}, [cellWidthsMap, tableGridLayout]);

	const headersMapper = React.useCallback((_headerGroup) => {
		return (
			<div
				className={styles['simple-table__header-row']}
				key={_headerGroup.id}
				style={{ gridTemplateColumns }}
			>
				{_headerGroup.headers.map((_header) => {
					return (
						<BaseHeaderCell<T>
							areAllSortsDisabled={!!disableAllSortings}
							header={_header}
							key={_header.id}
							onColumnDrag={onColumnDrag}
							setHeaderReference={setHeaderReference}
						/>
					);
				})}
			</div>
		);
	}, [gridTemplateColumns, onColumnDrag, setHeaderReference, disableAllSortings]);

	const cellMapper = React.useCallback((_cell: Cell<T, unknown>) => {
		return (
			<BaseCell<T>
				cell={_cell}
				key={_cell.id}
			/>
		);
	}, []);

	const rowsMapper = React.useCallback((_row: Row<T>) => {
		const rowClassNameArray = [styles['simple-table__row']];

		if (rowClassName) {
			typeof rowClassName === 'string' ? rowClassNameArray.push(rowClassName) : rowClassNameArray.push(rowClassName(_row));
		}

		const original = _row.original as object;

		if (highlightDeletedRows && 'deletedAt' in original && !!original.deletedAt) {
			rowClassNameArray.push(styles.archived);
		}

		return (
			<div
				className={rowClassNameArray.join(' ')}
				key={_row.id}
				onDoubleClick={onRowDoubleClick?.bind(null, _row)}
				style={{ gridTemplateColumns }}
			>
				{_row.getVisibleCells().map(cellMapper)}
			</div>
		);
	}, [rowClassName, highlightDeletedRows, onRowDoubleClick, gridTemplateColumns, cellMapper]);

	const buttonsMapper = (_button: FooterButton) => {
		return (
			<Button
				icon={_button.iconName}
				key={_button.label}
				label={_button.label}
				onClick={_button.onClick}
				size="small"
				style="link"
			/>
		);
	};

	const showTitleRow = headerButtons.length || label;
	return (
		<>
			{showTitleRow && (
				<div className={styles['simple-table__title-row']}>
					{label && (
						<div className={styles['simple-table__title-row__label']}>
							<SegmentLabel label={label} tooltip={labelTooltip} tooltipPosition={!!labelTooltip ? 'right' : undefined} />
						</div>
					)}
					{!!headerButtons.length && (
						<div className={styles['simple-table__title-row__buttons']}>
							{headerButtons.map(buttonsMapper)}
						</div>
					)}
				</div>
			)}
			<div className={styles['simple-table']}>
				<div className={styles['simple-table__body']}>
					{table.getHeaderGroups().map(headersMapper)}
					{table.getSortedRowModel().rows.length
						? table.getSortedRowModel().rows.map(rowsMapper)
						: !bottomTable.getCoreRowModel().rows.length && (
							<div className={styles['simple-table__empty-table-message']}>
								{emptyTableMessage ?? 'No Rows Found.'}
							</div>
						)}
					{bottomTable.getCoreRowModel().rows.map(rowsMapper)}
				</div>
				{footerComponent && (
					<div className={styles['simple-table__footer-component']}>
						{footerComponent()}
					</div>
				)}
				{(!!footerButtonsLeft.length || !!footerButtonsRight.length) && (
					<div className={styles['simple-table__footer']}>
						<div className={styles['simple-table__footer-buttons']}>
							{footerButtonsLeft.map(buttonsMapper)}
						</div>
						<div className={styles['simple-table__footer-buttons']}>
							{footerButtonsRight.map(buttonsMapper)}
						</div>
					</div>
				)}
			</div>
		</>
	);
}

export default React.memo(SimpleTable) as typeof SimpleTable;
